import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import SearchField, { isSearchReady } from './SearchField';
import PagedPicker from './PagedPicker';
import Button from './ui/Button';
import AdminDeleteControl from './AdminDeleteControl';
import { deleteAdminDepartment } from '../api';

/**
 * One department, opened: the groups that belong to it.
 *
 * A department has no rights of its own — it is a scope, and what happens
 * inside it is decided by its groups. So this is what there is to show, and it
 * is a list rather than a set of ticks: which department a group belongs to is
 * part of what the group *is*, chosen when it is created, and there is no
 * endpoint that moves one.
 */
function AdminDepartmentRow({
  department,
  groups,
  isExpanded = false,
  onToggle,
  onChanged,
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [isBrowsing, setBrowsing] = useState(false);

  const ownGroups = groups.filter(
    (group) => group.department_id === department.departmentId,
  );

  const visibleGroups = isSearchReady(query)
    ? ownGroups.filter((group) =>
        group.group_name.toLowerCase().includes(query.trim().toLowerCase()),
      )
    : ownGroups;

  // Only an empty department can go, and never the platform scope.
  const isInUse =
    department.groupCount +
      department.aclArticleCount +
      department.aclNewsCount >
    0;

  const deleteBlockedReason = department.isPlatform
    ? t('adminPanel.departmentIsPlatform')
    : isInUse
      ? t('adminPanel.departmentNotEmpty', {
          groups: department.groupCount,
          articles: department.aclArticleCount,
          news: department.aclNewsCount,
        })
      : null;

  return (
    <div className="admin-user-row">
      <button
        type="button"
        className="admin-user-row-header"
        aria-expanded={isExpanded}
        onClick={onToggle}
      >
        <i
          className={`fa-solid fa-chevron-right admin-user-toggle-icon${
            isExpanded ? ' expanded' : ''
          }`}
        />

        <div className="admin-user-identity">
          <strong>{department.name}</strong>
          <span>
            {department.isPlatform
              ? t('adminPanel.platformScope')
              : t('adminPanel.departmentMembers', {
                  count: department.memberCount,
                })}
          </span>
        </div>

        <span className="admin-user-status active">
          {t('adminPanel.groupCount', { count: ownGroups.length })}
        </span>
      </button>

      {isExpanded && (
        <div className="admin-user-row-body">
          <div className="admin-user-section">
            <h4>{t('adminPanel.groupsInDepartment')}</h4>

            {department.description && (
              <p className="admin-user-section-hint">
                {department.description}
              </p>
            )}

            <div className="admin-user-search-row">
              <SearchField
                value={query}
                onChange={setQuery}
                placeholder={t('adminPanel.groupSearchPlaceholder')}
                ariaLabel={t('adminPanel.groupSearchAriaLabel')}
              />

              <Button
                variant="secondary"
                aria-pressed={isBrowsing}
                onClick={() => setBrowsing((browsing) => !browsing)}
              >
                <i className="fa-solid fa-list" />
                {isBrowsing
                  ? t('adminPanel.hideAllUsers')
                  : t('adminPanel.showAllUsers')}
              </Button>
            </div>

            {isBrowsing ? (
              <PagedPicker
                items={ownGroups}
                getKey={(group) => group.group_id}
                selectable={false}
                renderPrimary={(group) => group.group_name}
                renderSecondary={(group) =>
                  t('adminPanel.groupMembers', { count: group.members })
                }
              />
            ) : visibleGroups.length === 0 ? (
              <p className="admin-user-list-empty">
                {isSearchReady(query)
                  ? t('adminPanel.noGroupsFound')
                  : t('adminPanel.noGroupsInDepartment')}
              </p>
            ) : (
              <div className="admin-department-groups">
                {visibleGroups.map((group) => (
                  <div className="admin-department-group" key={group.group_id}>
                    <strong>{group.group_name}</strong>

                    <span className="admin-user-pick-login">
                      {t('adminPanel.groupMembers', {
                        count: group.members,
                      })}
                    </span>

                    <span className="admin-department-group-rights">
                      {group.rights.length > 0
                        ? group.rights.join(', ')
                        : t('adminPanel.noRightsGranted')}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* The button says why before anyone presses it; the server
              refuses anyway. */}
          <div className="admin-user-section">
            <AdminDeleteControl
              label={t('adminPanel.deleteDepartment')}
              disabledReason={deleteBlockedReason}
              confirmText={t('adminPanel.deleteDepartmentConfirm', {
                name: department.name,
              })}
              onDelete={async () => {
                await deleteAdminDepartment(department.departmentId);

                if (onChanged) {
                  await onChanged();
                }
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminDepartmentRow;
